/**
 * Account dialog: NeonModalDialog for managing user account settings.
 * Single-column layout with sections: Account Information, Broadcasts Default,
 * Post Options (conditional), Display Settings.
 * Uses Neon form components (NeonTextField, NeonSelectField, NeonCheckboxField).
 * Save button only enabled when form has changes.
 */

import { SimpleTime, translate } from '@ps-refarch-ux/mfe-utils';
import {
    NeonButton,
    NeonCheckboxField,
    NeonModalDialog,
    NeonSelectField,
    NeonTextField,
    NeonTimeField
} from '@ps-refarch-ux/neon';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import './account.scss';
import { useAccountData } from './hooks/useAccountData';
import type { AccountFormValues, FieldAccess } from './types/account-types';

const DUMMY_PASSWORD = '••••••••';
const DUMMY_PIN = '••••';

export interface AccountMfeContentProps {
    open: boolean;
    onClose: () => void;
}

function isEditable(access: FieldAccess | undefined): boolean {
    return access === 'EDITABLE';
}

export function AccountMfeContent({ open, onClose }: AccountMfeContentProps): ReactElement | null {
    const { data, loading, error, saving, save } = useAccountData();
    const [formValues, setFormValues] = useState<Partial<AccountFormValues>>({});
    const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

    const defaultValues = useMemo<Partial<AccountFormValues>>(() => {
        if (!data) return {};
        return {
            login: data.user.login,
            firstName: data.user.firstName,
            lastName: data.user.lastName,
            email: data.user.email,
            autoReportEmail: data.user.autoReportEmail,
            phoneUserId: data.user.phoneUserId,
            phone: data.user.phone,
            sms: data.user.sms,
            password: DUMMY_PASSWORD,
            pinCode: DUMMY_PIN,
            callEarly: data.defaults.callEarly,
            callLate: data.defaults.callLate,
            callMax: data.defaults.callMax,
            daysToRun: data.defaults.daysToRun,
            presenceSecretKey: data.defaults.presenceSecretKey,
            actionLinks: data.defaults.actionLinks,
            hideEmailTools: data.defaults.hideEmailTools,
            communicateUi: data.defaults.communicateUi,
            personalCallerId: ''
        };
    }, [data]);

    const currentValues = useMemo(() => ({
        ...defaultValues,
        ...formValues
    }), [defaultValues, formValues]);

    const hasChanges = touchedFields.size > 0;

    const showPostOptions = data
        ? data.permissions.hasPresence
        : false;

    const updateField = useCallback((name: string, value: string | number | boolean | undefined) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
        setTouchedFields((prev) => new Set(prev).add(name));
    }, []);

    const handleSave = useCallback(async () => {
        if (!data) return;

        const USER_FIELDS = new Set([
            'firstName', 'lastName', 'login', 'password',
            'email', 'autoReportEmail', 'phoneUserId', 'phone', 'sms', 'pinCode'
        ]);

        const userAccess = data.user.accountFieldAccess;
        const defaultsAccess = data.defaults.accountDefaultFieldAccess;

        const user: Record<string, unknown> = {};
        const defaults: Record<string, unknown> = {};

        for (const fieldName of touchedFields) {
            const value = currentValues[fieldName as keyof AccountFormValues];
            if (fieldName === 'password' && value === DUMMY_PASSWORD) continue;
            if (fieldName === 'pinCode' && value === DUMMY_PIN) continue;

            const userField = userAccess[fieldName as keyof typeof userAccess];
            const defaultsField = defaultsAccess[fieldName as keyof typeof defaultsAccess];
            const access = userField ?? defaultsField;
            if (!isEditable(access)) continue;

            if (USER_FIELDS.has(fieldName)) {
                // CSE expects "pin" not "pinCode"
                const key = fieldName === 'pinCode' ? 'pin' : fieldName;
                user[key] = value;
            } else {
                defaults[fieldName] = value;
            }
        }

        // If either call time changed, send both so CSE gets the pair
        if (defaults.callEarly || defaults.callLate) {
            if (!defaults.callEarly) {
                defaults.callEarly = currentValues.callEarly;
            }
            if (!defaults.callLate) {
                defaults.callLate = currentValues.callLate;
            }
        }

        const payload: Record<string, unknown> = {};
        if (Object.keys(user).length > 0) payload.user = user;
        if (Object.keys(defaults).length > 0) payload.defaults = defaults;

        if (Object.keys(payload).length === 0) return;

        const success = await save(payload);
        if (success) {
            setFormValues({});
            setTouchedFields(new Set());
            onClose();
        }
    }, [data, touchedFields, currentValues, save, onClose]);

    const handleClose = useCallback(() => {
        setFormValues({});
        setTouchedFields(new Set());
        onClose();
    }, [onClose]);

    if (!open) return null;

    const userAccess = data?.user.accountFieldAccess;
    const defaultsAccess = data?.defaults.accountDefaultFieldAccess;

    // callMax options: 1..permissions.callMax
    const callMaxOptions = useMemo(() => {
        if (!data) return [];
        return Array.from({ length: data.permissions.callMax }, (_, i) => ({
            text: `${i + 1}`,
            value: `${i + 1}`
        }));
    }, [data]);

    // daysToRun options: 1..permissions.maxDaysToRun
    const daysToRunOptions = useMemo(() => {
        if (!data) return [];
        return Array.from({ length: data.permissions.maxDaysToRun }, (_, i) => ({
            text: `${i + 1}`,
            value: `${i + 1}`
        }));
    }, [data]);

    const actionLinksOptions = useMemo(() => [
        { text: translate('smc-web|account|Icons and Text'), value: 'BOTH' },
        { text: translate('smc-web|account|Icons Only'), value: 'ICONS' },
        { text: translate('smc-web|account|Text Only'), value: 'TEXT' }
    ], []);

    const communicateUiOptions = useMemo(() => [
        { text: translate('smc-web|account|Classic'), value: 'OLD' },
        { text: translate('smc-web|account|New'), value: 'NEW' }
    ], []);

    return (
        <NeonModalDialog
            id="account-dialog"
            dataSize="large"
            dataAnimate={true}
            dataShowCloseButton={true}
            dataShowHeaderDivider={true}
            dataShowFooterDivider={true}
            neonDialogHasClosed={handleClose}
        >
            <div data-slot="dialog-header-title">
                <h2 className="__account__dialog-title">
                    {translate('smc-web|account|Account')}
                </h2>
            </div>

            <div data-slot="dialog-body">
                {loading && (
                    <div className="__account__loading">
                        {translate('smc-web|account|Loading account data...')}
                    </div>
                )}

                {error && (
                    <div className="__account__error">
                        <p>{error}</p>
                    </div>
                )}

                {data && (
                    <div className="__account__form-column">
                        {/* ── Account Information ── */}
                        <h3 className="__account__section-heading">
                            {translate('smc-web|account|Login Credentials')}
                        </h3>
                        <hr className="__account__divider" />
                        <div className="neon-3_0_0-grid-container __account__section-grid">
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonTextField
                                    id="account-login"
                                    dataLabelText={translate('smc-web|account|Login')}
                                    dataIsRequired={true}
                                    modelValue={String(currentValues.login ?? '')}
                                    modelValueChange={(v) => updateField('login', v)}
                                    disabled={!isEditable(userAccess?.login)}
                                />
                            </div>
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonTextField
                                    id="account-password"
                                    dataLabelText={translate('smc-web|account|Password')}
                                    dataIsRequired={true}
                                    dataIsPassword={true}
                                    modelValue={String(currentValues.password ?? '')}
                                    modelValueChange={(v) => updateField('password', v)}
                                    disabled={!isEditable(userAccess?.password)}
                                />
                            </div>
                        </div>

                        {/* ── Personal Information ── */}
                        <h3 className="__account__section-heading __account__section-heading--spaced">
                            {translate('smc-web|account|Personal Information')}
                        </h3>
                        <hr className="__account__divider" />
                        <div className="neon-3_0_0-grid-container __account__section-grid">
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonTextField
                                    id="account-firstName"
                                    dataLabelText={translate('smc-web|account|First Name')}
                                    dataIsRequired={true}
                                    modelValue={String(currentValues.firstName ?? '')}
                                    modelValueChange={(v) => updateField('firstName', v)}
                                    disabled={!isEditable(userAccess?.firstName)}
                                />
                            </div>
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonTextField
                                    id="account-lastName"
                                    dataLabelText={translate('smc-web|account|Last Name')}
                                    dataIsRequired={true}
                                    modelValue={String(currentValues.lastName ?? '')}
                                    modelValueChange={(v) => updateField('lastName', v)}
                                    disabled={!isEditable(userAccess?.lastName)}
                                />
                            </div>
                        </div>

                        {/* ── Contact Information ── */}
                        <h3 className="__account__section-heading __account__section-heading--spaced">
                            {translate('smc-web|account|Contact Information')}
                        </h3>
                        <hr className="__account__divider" />
                        <div className="neon-3_0_0-grid-container __account__section-grid">
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonTextField
                                    id="account-email"
                                    dataLabelText={translate('smc-web|account|Email')}
                                    dataIsRequired={true}
                                    dataIsEmail={true}
                                    modelValue={String(currentValues.email ?? '')}
                                    modelValueChange={(v) => updateField('email', v)}
                                    disabled={!isEditable(userAccess?.email)}
                                />
                            </div>
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonTextField
                                    id="account-autoReportEmail"
                                    dataLabelText={translate('smc-web|account|Auto Report Email')}
                                    dataIsEmail={true}
                                    modelValue={String(currentValues.autoReportEmail ?? '')}
                                    modelValueChange={(v) => updateField('autoReportEmail', v)}
                                    disabled={!isEditable(userAccess?.autoReportEmail)}
                                />
                            </div>
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonTextField
                                    id="account-phone"
                                    dataLabelText={translate('smc-web|account|Phone')}
                                    modelValue={String(currentValues.phone ?? '')}
                                    modelValueChange={(v) => updateField('phone', v)}
                                    disabled={!isEditable(userAccess?.phone)}
                                />
                            </div>
                            {data.permissions.customerHasSms && (
                                <div className="neon-3_0_0-grid-1-2">
                                    <NeonTextField
                                        id="account-sms"
                                        dataLabelText={translate('smc-web|account|SMS')}
                                        modelValue={String(currentValues.sms ?? '')}
                                        modelValueChange={(v) => updateField('sms', v)}
                                        disabled={!isEditable(userAccess?.sms)}
                                    />
                                </div>
                            )}
                        </div>

                        {/* ── Phone User Information ── */}
                        <h3 className="__account__section-heading __account__section-heading--spaced">
                            {translate('smc-web|account|Phone User Information')}
                        </h3>
                        <hr className="__account__divider" />
                        <div className="neon-3_0_0-grid-container __account__section-grid">
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonTextField
                                    id="account-phoneUserId"
                                    dataLabelText={translate('smc-web|account|Phone User ID')}
                                    modelValue={String(currentValues.phoneUserId ?? '')}
                                    modelValueChange={(v) => updateField('phoneUserId', v)}
                                    disabled={!isEditable(userAccess?.phoneUserId)}
                                />
                            </div>
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonTextField
                                    id="account-pinCode"
                                    dataLabelText={translate('smc-web|account|PIN')}
                                    dataIsPassword={true}
                                    modelValue={String(currentValues.pinCode ?? '')}
                                    modelValueChange={(v) => updateField('pinCode', v)}
                                    disabled={!isEditable(userAccess?.pinCode)}
                                />
                            </div>
                        </div>

                        {/* ── Broadcasts Default ── */}
                        <h3 className="__account__section-heading __account__section-heading--spaced">
                            {translate('smc-web|account|Broadcasts Default')}
                        </h3>
                        <hr className="__account__divider" />
                        <div className="neon-3_0_0-grid-container __account__section-grid">
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonTimeField
                                    id="account-callEarly"
                                    dataLabelText={translate('smc-web|account|Earliest Call Time')}
                                    dataIsRequired={true}
                                    modelValue={new SimpleTime(String(currentValues.callEarly ?? '00:00:00'))}
                                    modelValueChange={(v: SimpleTime) => updateField('callEarly', v.to24HourTime())}
                                    disabled={!isEditable(defaultsAccess?.callEarly)}
                                />
                            </div>
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonTimeField
                                    id="account-callLate"
                                    dataLabelText={translate('smc-web|account|Latest Call Time')}
                                    dataIsRequired={true}
                                    modelValue={new SimpleTime(String(currentValues.callLate ?? '00:00:00'))}
                                    modelValueChange={(v: SimpleTime) => updateField('callLate', v.to24HourTime())}
                                    disabled={!isEditable(defaultsAccess?.callLate)}
                                />
                            </div>
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonSelectField
                                    id="account-callMax"
                                    dataLabelText={translate('smc-web|account|Max Call Attempts')}
                                    dataIsRequired={true}
                                    modelValue={String(currentValues.callMax ?? '')}
                                    modelValueChange={(v) => updateField('callMax', v ? Number(v) : undefined)}
                                    options={callMaxOptions}
                                    disabled={!isEditable(defaultsAccess?.callMax)}
                                />
                            </div>
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonSelectField
                                    id="account-daysToRun"
                                    dataLabelText={translate('smc-web|account|Days to Run Broadcast')}
                                    dataIsRequired={true}
                                    modelValue={String(currentValues.daysToRun ?? '')}
                                    modelValueChange={(v) => updateField('daysToRun', v ? Number(v) : undefined)}
                                    options={daysToRunOptions}
                                    disabled={!isEditable(defaultsAccess?.daysToRun)}
                                />
                            </div>
                        </div>

                        {/* ── Post Options (conditional) ── */}
                        {showPostOptions && (
                            <>
                                <h3 className="__account__section-heading __account__section-heading--spaced">
                                    {translate('smc-web|account|Post Options')}
                                </h3>
                                <hr className="__account__divider" />
                                <div className="neon-3_0_0-grid-container __account__section-grid">
                                    <div className="neon-3_0_0-grid-1-1">
                                        <NeonTextField
                                            id="account-presenceSecretKey"
                                            dataLabelText={translate('smc-web|account|Presence Secret Key')}
                                            modelValue={String(currentValues.presenceSecretKey ?? '')}
                                            modelValueChange={(v) => updateField('presenceSecretKey', v)}
                                            disabled={!isEditable(defaultsAccess?.presenceSecretKey)}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ── Display Settings ── */}
                        <h3 className="__account__section-heading __account__section-heading--spaced">
                            {translate('smc-web|account|Display Settings')}
                        </h3>
                        <hr className="__account__divider" />
                        <div className="neon-3_0_0-grid-container __account__section-grid">
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonSelectField
                                    id="account-actionLinks"
                                    dataLabelText={translate('smc-web|account|Action Links')}
                                    modelValue={String(currentValues.actionLinks ?? '')}
                                    modelValueChange={(v) => updateField('actionLinks', v)}
                                    options={actionLinksOptions}
                                    disabled={!isEditable(defaultsAccess?.actionLinks)}
                                />
                            </div>
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonSelectField
                                    id="account-communicateUi"
                                    dataLabelText={translate('smc-web|account|Communicate UI')}
                                    modelValue={String(currentValues.communicateUi ?? '')}
                                    modelValueChange={(v) => updateField('communicateUi', v)}
                                    options={communicateUiOptions}
                                    disabled={!isEditable(defaultsAccess?.communicateUi)}
                                />
                            </div>
                            <div className="neon-3_0_0-grid-1-2">
                                <NeonCheckboxField
                                    id="account-hideEmailTools"
                                    dataLabelText={translate('smc-web|account|Hide Email Tools')}
                                    modelValue={currentValues.hideEmailTools ? ['hideEmailTools'] : []}
                                    modelValueChange={(v) => updateField('hideEmailTools', (v ?? []).includes('hideEmailTools'))}
                                    options={[{
                                        id: 'hideEmailTools',
                                        labelText: translate('smc-web|account|Hide Email Tools'),
                                        name: 'hideEmailTools'
                                    }]}
                                    disabled={!isEditable(defaultsAccess?.hideEmailTools)}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div data-slot="dialog-button-footer">
                <div className="__account__dialog-actions">
                    <NeonButton
                        id="account-cancel-btn"
                        dataType="secondary"
                        dataText={translate('smc-web|account|Cancel')}
                        onClick={handleClose}
                    />
                    <NeonButton
                        id="account-save-btn"
                        dataType="primary"
                        dataText={saving ? translate('smc-web|account|Saving...') : translate('smc-web|account|Save')}
                        disabled={!hasChanges || saving}
                        onClick={handleSave}
                        dataIsLoading={saving}
                    />
                </div>
            </div>
        </NeonModalDialog>
    );
}
